import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import './AuthPages.css';

export default function SignupPage({ navigate }) {
  const { signup, loading } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState({});
  const [strength, setStrength] = useState(0);

  const calcStrength = (p) => {
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 6) e.password = 'Minimum 6 characters';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const res = await signup(form.name.trim(), form.email, form.password);
    if (res.success) {
      toast.success('🎉 Account created! Welcome to FreshMart!');
      navigate('home');
    } else {
      toast.error(res.error);
    }
  };

  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['', '#EF5350', '#FF9800', '#FFC107', '#4CAF50'];

  return (
    <div className="auth-page">
      <div className="auth-split">
        <div className="auth-visual signup-visual">
          <div className="auth-visual-content">
            <div className="auth-logo" onClick={() => navigate('home')}>🌿 FreshMart</div>
            <h2>Join 10,000+<br/><span>Happy Customers</span></h2>
            <p>Create your account and start ordering fresh groceries with lightning-fast delivery right to your door.</p>
            <div className="testimonials">
              {[
                { name: 'Priya S.', text: 'Best grocery app! Always fresh and on time.', avatar: 'P' },
                { name: 'Rahul M.', text: 'Love the variety and quick delivery!', avatar: 'R' },
                { name: 'Anjali K.', text: 'Organic products are superb quality!', avatar: 'A' },
              ].map(t => (
                <div className="testimonial-card" key={t.name}>
                  <div className="testi-avatar">{t.avatar}</div>
                  <div>
                    <p className="testi-text">"{t.text}"</p>
                    <p className="testi-name">— {t.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="auth-form-side">
          <div className="auth-form-wrap">
            <div className="auth-form-header">
              <button className="auth-back" onClick={() => navigate('home')}>← Back to Home</button>
              <h1>Create Account ✨</h1>
              <p>Start your fresh journey today</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className={`input-group ${errors.name ? 'error' : ''}`}>
                <label>Full Name</label>
                <div className="input-wrap">
                  <span className="input-icon">👤</span>
                  <input type="text" placeholder="Arjun Sharma" value={form.name}
                    onChange={e => { setForm(f => ({...f, name: e.target.value})); setErrors({}); }} />
                </div>
                {errors.name && <span className="err-msg">{errors.name}</span>}
              </div>

              <div className={`input-group ${errors.email ? 'error' : ''}`}>
                <label>Email Address</label>
                <div className="input-wrap">
                  <span className="input-icon">✉️</span>
                  <input type="email" placeholder="you@example.com" value={form.email}
                    onChange={e => { setForm(f => ({...f, email: e.target.value})); setErrors({}); }} />
                </div>
                {errors.email && <span className="err-msg">{errors.email}</span>}
              </div>

              <div className={`input-group ${errors.password ? 'error' : ''}`}>
                <label>Password</label>
                <div className="input-wrap">
                  <span className="input-icon">🔒</span>
                  <input type={showPass ? 'text' : 'password'} placeholder="Create a strong password"
                    value={form.password}
                    onChange={e => { setForm(f => ({...f, password: e.target.value})); setStrength(calcStrength(e.target.value)); setErrors({}); }} />
                  <button type="button" className="show-pass" onClick={() => setShowPass(!showPass)}>
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
                {form.password && (
                  <div className="strength-bar">
                    <div className="strength-track">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="strength-seg" style={{ background: i <= strength ? strengthColors[strength] : '#E8EDF2' }} />
                      ))}
                    </div>
                    <span style={{ color: strengthColors[strength], fontSize: 12, fontWeight: 600 }}>{strengthLabels[strength]}</span>
                  </div>
                )}
                {errors.password && <span className="err-msg">{errors.password}</span>}
              </div>

              <div className={`input-group ${errors.confirm ? 'error' : ''}`}>
                <label>Confirm Password</label>
                <div className="input-wrap">
                  <span className="input-icon">🔐</span>
                  <input type={showPass ? 'text' : 'password'} placeholder="Repeat your password"
                    value={form.confirm}
                    onChange={e => { setForm(f => ({...f, confirm: e.target.value})); setErrors({}); }} />
                </div>
                {errors.confirm && <span className="err-msg">{errors.confirm}</span>}
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? <span className="loading-dots"><span/><span/><span/></span> : '→ Create Account'}
              </button>
            </form>

            <div className="auth-divider"><span>or</span></div>
            <button className="google-btn" onClick={() => toast('Google sign-in coming soon!')}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" width="20" />
              Sign up with Google
            </button>

            <p className="auth-switch">
              Already have an account?{' '}
              <button onClick={() => navigate('login')}>Sign In →</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
